import { Box } from "@mui/material";

/**
 * The standard clip-rect recipe for taking an element out of the visual layout while leaving it
 * fully readable in the accessibility tree.
 *
 * Declared here rather than imported from `@mui/utils` because that export is typed as
 * `CSSProperties`, which does not satisfy `SxProps<Theme>` (no index signature) and so has to be
 * cast at every call site. The declaration is nine lines and this way it type-checks directly.
 *
 * `display: none` and `visibility: hidden` are NOT substitutes: both remove the node from the
 * accessibility tree, which is the opposite of what this is for. Neither is `opacity: 0`, which
 * fails the other way — it keeps the node readable *and* keeps whatever it was meant to replace
 * readable too.
 *
 * The `position: absolute` is doing double duty and must not be dropped: besides enabling the clip,
 * it takes the node OUT OF FLOW. That is what lets this be dropped into a `Stack` or a `gap` flex
 * container without shifting the visible children — an in-flow 1px node still claims a flex slot
 * and still collects the container's spacing margin. Exported so wrappers that need to put a
 * `role`/`aria-live` on the hidden node itself (see `AsyncStatus`) get the same guarantee.
 */
export const visuallyHiddenSx = {
	border: 0,
	clip: "rect(0 0 0 0)",
	height: "1px",
	margin: "-1px",
	overflow: "hidden",
	padding: 0,
	position: "absolute",
	whiteSpace: "nowrap",
	width: "1px",
} as const;

interface VisuallyHiddenProps {
	children: React.ReactNode;
	/** Set when another element needs to point at this text via `aria-labelledby`. */
	id?: string;
	/** Defaults to `span` so the text can sit inside inline content without breaking layout. */
	component?: "span" | "div";
}

/**
 * Text that is present in the accessibility tree but not painted.
 *
 * This is the escape hatch for state the UI encodes visually — a color, an icon, a badge
 * position — that a browser agent or screen reader would otherwise never see. Prefer making the
 * visible text carry the meaning; reach for this only when the design genuinely cannot show the
 * words.
 */
export function VisuallyHidden({ children, id, component = "span" }: VisuallyHiddenProps) {
	return (
		<Box component={component} id={id} sx={visuallyHiddenSx}>
			{children}
		</Box>
	);
}
